/** @format */

// Core form data types
export interface Address {
  street: string;
  aptUnite: string;
  city: string;
  state: string;
  postalCode?: string;
  zipCode?: string;
  country?: string;
}

export interface PersonInfo {
  firstName: string;
  lastName: string;
  dob?: string;
  dateOfBirth?: string;
  SSN?: string;
  ssn?: string;
}

export interface SpouseInfo extends PersonInfo {}

export interface DependentInfo extends PersonInfo {
  id?: string;
}

export interface PlanInfo {
  carrier: string;
  isPostDate: boolean | null;
  billingDate: string;
  premium: number | string;
  status: string;
  planName?: string;
  effectiveDate?: string;
  applicationNumber?: string;
  policyNumber?: string;
  notes?: string;
}

export interface AncillaryInfo extends PlanInfo {}
export interface MajorMedInfo extends PlanInfo {}
export interface PrivateMedInfo extends PlanInfo {}

// Main form data structure
export interface SaleSubmissionFormData {
  leadId?: string;
  request: boolean;
  firstName: string;
  lastName: string;
  phoneNumber: string | number;
  bestPhoneNumber: string | null;
  dateOfBirth: string;
  dob?: string;
  SSN?: string;
  ssn?: string;
  email: string;
  typeOfWork?: string;
  occupation?: string;
  address: Address;
  spouseInfo: SpouseInfo;
  dependentsInfo: DependentInfo[];
  householdIncome: number | null;
  QLE: string;
  planType: string[];
  ancillaries: AncillaryInfo[];
  privateMed: PrivateMedInfo;
  majorMed: MajorMedInfo;
  memberId: string;
  FFMID?: string | null;
  FFMId?: string | null;
  healthSherpa: string;
  NPN: string;
  AOR: string;
  enrollmentFee: number | null;
  benefitsURL: string;
  notes: string;
  userId?: string;
}

// General questions for form logic
export interface GeneralQuestions {
  dependents: boolean;
  status: 'single' | 'married';
}

// User/Agent types
export interface User {
  _id: string;
  firstname?: string;
  firstName?: string;
  lastname?: string;
  lastName?: string;
  username: string;
  email: string;
  role: string;
  officeId: string;
  division: string[];
  fullName?: string;
}

// API response types
export interface AOR {
  _id: string;
  firstName: string;
  name: string;
}

export interface NPN {
  _id: string;
  firstName: string;
  name: string;
  number: string;
}

export interface SubmissionResponse {
  success: boolean;
  message: string;
  id?: string;
}

// Hook types
export interface UseFormDataReturn {
  formData: SaleSubmissionFormData;
  generalQ: GeneralQuestions;
  handleChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  handleGeneralQChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleAncillaryChange: (index: number, e: React.ChangeEvent<HTMLInputElement>) => void;
  handleDependentChange: (index: number, e: React.ChangeEvent<HTMLInputElement>) => void;
  addAncillary: () => void;
  removeAncillary: (index: number) => void;
  addDependent: () => void;
  removeDependent: (index: number) => void;
  resetForm: () => void;
}

export interface UseApiDataReturn {
  users: User[];
  aors: AOR[];
  npns: NPN[];
  loading: boolean;
  error: string | null;
}

// Constants
export const PLAN_TYPES = ['Private Med', 'Ancillary', 'Major Medical'] as const;
export const QLE_OPTIONS = [
  'Loss of health coverage',
  'Changes in household',
  'Changes in residence',
  'Other qualifying events',
  'Change of Income',
] as const;
export const SHERPA_OPTIONS = [
  'AOBG',
  'Premier',
  'Allstate',
  'Messer',
  'Referal',
  'Uncontracted',
] as const;
export const ENROLLMENT_FEES = [0, 20, 25, 27.5, 30, 50, 80, 99, 125] as const;

export type PlanType = typeof PLAN_TYPES[number];
export type QLEOption = typeof QLE_OPTIONS[number];
export type SherpaOption = typeof SHERPA_OPTIONS[number];
export type EnrollmentFee = typeof ENROLLMENT_FEES[number];